import { authHeaders } from "@/lib/api";
import { InventoryApiError } from "./api";

export interface AllocationOrder { order_id: string; status: string }
export interface SerialAllocation { allocation_id: number; unit_id: number; serial: string; state: string }
export interface ManifestLine { order_line_id: number; product_id: number; label: string; quantity: string; policy: string; allocations: SerialAllocation[] }
export interface SerialManifest {
  order_id: string; order_status: string; document_label: string; version: number;
  state: string; lines: ManifestLine[]; warnings: string[];
}
export interface AllocationUnit { unit_id: number; product_id: number; serial: string; box_id: string; location_id: number; row_version: number; status: string }
export interface AllocationOrdersPage { orders: AllocationOrder[]; has_more: boolean; offset: number }
export interface AllocationUnitsPage { units: AllocationUnit[]; has_more: boolean; offset: number }
export interface SerialException { exception_id?: number; order_id: string; code: string; detail?: string; created_at?: string; export_id?: string }
export interface SerialExceptionsPage {
  export_exceptions: SerialException[]; has_more: boolean; reimport_exceptions: SerialException[];
  reimport_has_more: boolean; offset: number; reconciliation: string;
}
export interface AllocateSerialsInput { order_line_id: number; unit_ids: number[]; expected_version: number }
const BASE = "/api/admin";
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { ...init, credentials: "include", headers: { "Content-Type": "application/json", ...init.headers, ...authHeaders() } });
  if (!r.ok) {
    let detail = "";
    try { const body = await r.json(); detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail ?? ""); } catch { /* Preserve non-JSON HTTP status. */ }
    throw new InventoryApiError(r.status, detail);
  }
  return r.json() as Promise<T>;
}
function integer(value: number, minimum = 0) {
  if (!Number.isSafeInteger(value) || value < minimum) throw new Error("Invalid integer");
}
function paging(limit: number, offset: number) {
  integer(limit, 1); integer(offset);
  if (limit > 200 || offset > 1000000) throw new Error("Invalid page bounds");
  return new URLSearchParams({ limit: String(limit), offset: String(offset) });
}
const orderPath = (id: string) => `/orders/${encodeURIComponent(id)}`;
export async function listAllocationOrders(limit = 50, offset = 0): Promise<AllocationOrdersPage> {
  return request(`/inventory/serial-allocation-orders?${paging(limit, offset)}`);
}
export async function getSerialManifest(orderId: string): Promise<SerialManifest> {
  return request(`${orderPath(orderId)}/serial-manifest`);
}
export async function listAllocationUnits(productId: number, serial = "", limit = 100, offset = 0): Promise<AllocationUnitsPage> {
  integer(productId, 1);
  if (serial.length > 160) throw new Error("Serial filter exceeds limit");
  const params = new URLSearchParams({ serial, ...Object.fromEntries(paging(limit, offset)) });
  return request(`/inventory/serial-allocation-units/${productId}?${params}`);
}
export async function listSerialExceptions(limit = 50, offset = 0): Promise<SerialExceptionsPage> {
  return request(`/inventory/invoice-serial-exceptions?${paging(limit, offset)}`);
}
function writeManifest(orderId: string, suffix: string, body: unknown, key: string): Promise<SerialManifest> {
  if (!/^[A-Za-z0-9._:-]{1,160}$/.test(key)) throw new Error("Invalid idempotency key");
  return request(`${orderPath(orderId)}/serial-allocations${suffix}`, { method: "POST", body: JSON.stringify(body), headers: { "Idempotency-Key": key, "X-Inventory-Confirm": "true" } });
}
export async function allocateOrderSerials(orderId: string, input: AllocateSerialsInput, key: string): Promise<SerialManifest> {
  const { order_line_id, unit_ids, expected_version } = input;
  integer(order_line_id, 1); integer(expected_version); unit_ids.forEach(id => integer(id, 1));
  if (!unit_ids.length || unit_ids.length > 10000 || new Set(unit_ids).size !== unit_ids.length) throw new Error("Invalid unit selection");
  return writeManifest(orderId, "", { order_line_id, unit_ids: [...unit_ids], expected_version }, key);
}
export async function finalizeOrderSerials(orderId: string, expected_version: number, key: string): Promise<SerialManifest> {
  integer(expected_version);
  return writeManifest(orderId, "/finalize", { expected_version }, key);
}
/** Decimal strings stay authoritative; only safe whole quantities enable serial controls. */
export function serialQuantity(quantity: string): number | null {
  if (!/^\d+(?:\.0+)?$/.test(quantity)) return null;
  const value = Number(quantity);
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}
